
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function FinancialAlert() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'alerts' | 'settings'>('alerts');

  const alerts = [
    {
      id: 1,
      type: 'warning',
      title: 'تجاوز الميزانية الشهرية',
      message: 'لقد تجاوزت ميزانيتك الشهرية بمبلغ 500 ريال',
      date: '2024-01-15',
      icon: 'ri-alert-line',
      color: 'bg-orange-100 text-orange-600 border-orange-200'
    },
    {
      id: 2,
      type: 'success',
      title: 'هدف الادخار محقق',
      message: 'تهانينا! لقد حققت هدف الادخار لهذا الشهر',
      date: '2024-01-14',
      icon: 'ri-check-line',
      color: 'bg-green-100 text-green-600 border-green-200'
    },
    {
      id: 3,
      type: 'info',
      title: 'فاتورة مستحقة',
      message: 'فاتورة الكهرباء مستحقة خلال 3 أيام',
      date: '2024-01-13',
      icon: 'ri-information-line',
      color: 'bg-blue-100 text-blue-600 border-blue-200'
    },
    {
      id: 4,
      type: 'error',
      title: 'رصيد منخفض',
      message: 'رصيدك الحالي أقل من الحد الأدنى المحدد',
      date: '2024-01-12',
      icon: 'ri-error-warning-line',
      color: 'bg-red-100 text-red-600 border-red-200'
    }
  ];

  const [budgetLimit, setBudgetLimit] = useState('3000');
  const [lowBalanceAlert, setLowBalanceAlert] = useState('500');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [weeklyReports, setWeeklyReports] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="fixed top-0 w-full bg-white shadow-sm z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100"
          >
            <i className="ri-arrow-right-line text-xl text-gray-700"></i>
          </button>
          <h1 className="text-xl font-bold text-gray-800">التنبيهات المالية</h1>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="fixed top-16 w-full bg-white border-b border-gray-200 z-40">
        <div className="flex">
          <button
            onClick={() => setActiveTab('alerts')}
            className={`flex-1 py-4 px-6 font-medium transition-all ${
              activeTab === 'alerts'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500'
            }`}
          >
            التنبيهات
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 py-4 px-6 font-medium transition-all ${
              activeTab === 'settings'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500'
            }`}
          >
            الإعدادات
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="pt-32 pb-24 px-6">
        {activeTab === 'alerts' ? (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <div key={alert.id} className={`bg-white rounded-2xl p-4 border-2 ${alert.color}`}>
                <div className="flex items-start space-x-3 space-x-reverse">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${alert.color}`}>
                    <i className={`${alert.icon} text-lg`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-1">{alert.title}</h3>
                    <p className="text-gray-600 text-sm mb-2">{alert.message}</p>
                    <p className="text-gray-400 text-xs">{alert.date}</p>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>
              </div>
            ))}

            {alerts.length === 0 && (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <i className="ri-notification-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-gray-500">لا توجد تنبيهات جديدة</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Budget Alerts */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">تنبيهات الميزانية</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">حد الميزانية الشهرية</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={budgetLimit}
                      onChange={(e) => setBudgetLimit(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">ريال</span>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">تنبيه الرصيد المنخفض</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={lowBalanceAlert}
                      onChange={(e) => setLowBalanceAlert(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">ريال</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">إعدادات التنبيهات</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">تنبيهات البريد الإلكتروني</p>
                    <p className="text-gray-500 text-sm">استقبال التنبيهات عبر البريد</p>
                  </div>
                  <button
                    onClick={() => setEmailNotifications(!emailNotifications)}
                    className={`relative w-12 h-6 rounded-full transition-all ${
                      emailNotifications ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${
                        emailNotifications ? 'right-0.5' : 'right-6'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">الإشعارات الفورية</p>
                    <p className="text-gray-500 text-sm">استقبال إشعارات فورية على الجهاز</p>
                  </div>
                  <button
                    onClick={() => setPushNotifications(!pushNotifications)}
                    className={`relative w-12 h-6 rounded-full transition-all ${
                      pushNotifications ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${
                        pushNotifications ? 'right-0.5' : 'right-6'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">التقارير الأسبوعية</p>
                    <p className="text-gray-500 text-sm">استقبال ملخص أسبوعي للمصروفات</p>
                  </div>
                  <button
                    onClick={() => setWeeklyReports(!weeklyReports)}
                    className={`relative w-12 h-6 rounded-full transition-all ${
                      weeklyReports ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${
                        weeklyReports ? 'right-0.5' : 'right-6'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <button className="w-full bg-blue-600 text-white py-4 rounded-2xl font-semibold text-lg transform transition-all duration-200 hover:scale-105 active:scale-95">
              حفظ الإعدادات
            </button>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 w-full bg-white border-t border-gray-200">
        <div className="grid grid-cols-4 py-2">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-home-line text-gray-400 text-lg"></i>
            </div>
            <span className="text-xs text-gray-400">الرئيسية</span>
          </button>
          <button 
            onClick={() => navigate('/expenses')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-arrow-down-line text-gray-400 text-lg"></i>
            </div>
            <span className="text-xs text-gray-400">المصروفات</span>
          </button>
          <button 
            onClick={() => navigate('/reports')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-bar-chart-line text-gray-400 text-lg"></i>
            </div>
            <span className="text-xs text-gray-400">التقارير</span>
          </button>
          <button 
            onClick={() => navigate('/financial-alert')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-notification-line text-blue-600 text-lg"></i>
            </div>
            <span className="text-xs text-blue-600 font-medium">التنبيهات</span>
          </button>
        </div>
      </div>
    </div>
  );
}
